import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  /** Prevent dismissal while an irreversible request is in flight. */
  closeDisabled?: boolean;
  /** Announces that the dialog is awaiting a server result. */
  busy?: boolean;
}

/**
 * A modal built on the native <dialog> element, so focus trapping, Escape and the
 * top-layer stacking come from the platform rather than from a hand-rolled approximation.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  closeDisabled = false,
  busy = false,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    // jsdom (and older Safari) has no showModal; fall back to the `open` attribute so the
    // content is still rendered and assertable rather than silently absent.
    if (open && !element.open) {
      if (typeof element.showModal === "function") element.showModal();
      else element.setAttribute("open", "");
    }
    if (!open && element.open) {
      if (typeof element.close === "function") element.close();
      else element.removeAttribute("open");
    }
    // A dialog unmounted while still open (a parent conditionally rendering
    // `{x ? <Dialog open …/> : null}`) must close BEFORE the element leaves the DOM:
    // element.close() is what makes the platform return focus to the previously focused
    // element, so removing an open modal instead leaves focus on <body>. Closing a dialog
    // that is already closed is a no-op, so the open-prop transitions are unaffected.
    return () => {
      if (element.open && typeof element.close === "function") element.close();
    };
  }, [open]);

  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      event.preventDefault();
      if (!closeDisabled) onClose();
    },
    [closeDisabled, onClose],
  );

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      aria-labelledby="radar-dialog-title"
      aria-describedby={description ? "radar-dialog-description" : undefined}
      aria-busy={busy || undefined}
      onCancel={handleCancel}
      className={cn(
        "m-auto w-[min(32rem,calc(100vw-2rem))] rounded-card border border-line bg-surface p-0 text-ink shadow-raised backdrop:bg-ink/40",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-4 p-5 pb-3">
        <div className="min-w-0 flex-1 flex flex-col gap-1 break-words">
          <h2 id="radar-dialog-title" className="text-base font-semibold">
            {title}
          </h2>
          {description ? (
            <p id="radar-dialog-description" className="text-sm text-ink-muted">
              {description}
            </p>
          ) : null}
        </div>
        <Button variant="ghost" size="icon" className="h-11 w-11 shrink-0" onClick={onClose} disabled={closeDisabled} aria-label="Close">
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      {children ? <div className="px-5 pb-4 text-sm">{children}</div> : null}
      {footer ? (
        <div className="flex items-center justify-end gap-3 border-t border-line px-5 py-4">
          {footer}
        </div>
      ) : null}
    </dialog>
  );
}
